// Typescript CodeGen Prototype "Expectant Sunshine"
// Transform Version: v1.0.0
// Template Version:  v1.0.0
//
// -- DO NOT MODIFY THIS FILE! --

/* tslint:disable:no-trailing-whitespace */

import * as _ from 'lodash';
import * as Wellspring from '@greenheck/wellspring';
import * as Axon from '@jlekie/axon';
import { v4 as Uuid } from 'uuid';
import * as ParseHelpers from '@jlekie/parse-helpers';
import * as Zod from 'zod';

export enum DataTypes {
    String,
    Integer,
    Decimal
}

export function isTableModel(source: any): source is ITable {
    // Method Implementation: simple
    return source.modelName === 'Table';
}

export function isColumnModel(source: any): source is IColumn {
    // Method Implementation: simple
    return source.modelName === 'Column';
}

export function isRowModel(source: any): source is IRow {
    // Method Implementation: simple
    return source.modelName === 'Row';
}

export function isFieldModel(source: any): source is IField {
    // Method Implementation: simple
    return source.modelName === 'Field';
}

export function isStringFieldModel(source: any): source is IStringField {
    // Method Implementation: simple
    return source.modelName === 'StringField';
}

export function isIntegerFieldModel(source: any): source is IIntegerField {
    // Method Implementation: simple
    return source.modelName === 'IntegerField';
}

export function isDecimalFieldModel(source: any): source is IDecimalField {
    // Method Implementation: simple
    return source.modelName === 'DecimalField';
}

export function isBooleanFieldModel(source: any): source is IBooleanField {
    // Method Implementation: simple
    return source.modelName === 'BooleanField';
}

export interface TableZodSchemaShape {
    columns?: ColumnZodSchemaShape[];

    rows?: RowZodSchemaShape[];
}

export interface ColumnZodSchemaShape {
    name?: string;

    dataType?: 'String' | 'Integer' | 'Decimal';
}

export interface RowZodSchemaShape {
    fields?: FieldZodSchemaShape[];
}

export interface FieldZodSchemaShape {
    fieldType?: string;

    name?: string;
}

export interface StringFieldZodSchemaShape {
    fieldType?: string;

    name?: string;

    value?: string;
}

export interface IntegerFieldZodSchemaShape {
    fieldType?: string;

    name?: string;

    value?: number;
}

export interface DecimalFieldZodSchemaShape {
    fieldType?: string;

    name?: string;

    value?: number;
}

export interface BooleanFieldZodSchemaShape {
    fieldType?: string;

    name?: string;

    value?: boolean;
}

// Artifact Implementation: artifacts/zodSchemaType
export const TableZodSchema: Zod.ZodSchema<TableZodSchemaShape> = Zod.lazy(() => Zod.object({
    columns: Zod.array(ColumnZodSchema).optional(),
    rows: Zod.array(RowZodSchema).optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const ColumnZodSchema: Zod.ZodSchema<ColumnZodSchemaShape> = Zod.lazy(() => Zod.object({
    name: Zod.string().optional(),
    dataType: Zod.enum([ 'String', 'Integer', 'Decimal' ]).optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const RowZodSchema: Zod.ZodSchema<RowZodSchemaShape> = Zod.lazy(() => Zod.object({
    fields: Zod.array(FieldZodSchema).optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const FieldZodSchema: Zod.ZodSchema<FieldZodSchemaShape> = Zod.lazy(() => Zod.object({
    fieldType: Zod.string(),
    name: Zod.string().optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const StringFieldZodSchema: Zod.ZodSchema<StringFieldZodSchemaShape> = Zod.lazy(() => Zod.object({
    fieldType: Zod.string(),
    name: Zod.string().optional(),
    value: Zod.string().optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const IntegerFieldZodSchema: Zod.ZodSchema<IntegerFieldZodSchemaShape> = Zod.lazy(() => Zod.object({
    fieldType: Zod.string(),
    name: Zod.string().optional(),
    value: Zod.number().optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const DecimalFieldZodSchema: Zod.ZodSchema<DecimalFieldZodSchemaShape> = Zod.lazy(() => Zod.object({
    fieldType: Zod.string(),
    name: Zod.string().optional(),
    value: Zod.number().optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const BooleanFieldZodSchema: Zod.ZodSchema<BooleanFieldZodSchemaShape> = Zod.lazy(() => Zod.object({
    fieldType: Zod.string(),
    name: Zod.string().optional(),
    value: Zod.boolean().optional()
}).nonstrict());

export interface ITable extends Wellspring.IModel {
    readonly modelName: string;

    readonly columns: Column[];

    readonly rows: Row[];

    readonly isColumnsDefined: boolean;

    readonly isRowsDefined: boolean;
}

export interface IColumn extends Wellspring.IModel {
    readonly modelName: string;

    readonly name: string;

    readonly dataType: DataTypes;

    readonly isNameDefined: boolean;

    readonly isDataTypeDefined: boolean;
}

export interface IRow extends Wellspring.IModel {
    readonly modelName: string;

    readonly fields: AField[];

    readonly isFieldsDefined: boolean;
}

export interface IField extends Wellspring.IModel {
    readonly modelName: string;

    readonly name: string;

    readonly isNameDefined: boolean;
}

export interface IStringField extends IField, Wellspring.IModel {
    readonly modelName: string;

    readonly value: string;

    readonly isValueDefined: boolean;
}

export interface IIntegerField extends IField, Wellspring.IModel {
    readonly modelName: string;

    readonly value: number;

    readonly isValueDefined: boolean;
}

export interface IDecimalField extends IField, Wellspring.IModel {
    readonly modelName: string;

    readonly value: number;

    readonly isValueDefined: boolean;
}

export interface IBooleanField extends IField, Wellspring.IModel {
    readonly modelName: string;

    readonly value: boolean;

    readonly isValueDefined: boolean;
}

export class Table implements ITable {
    public static readonly modelName: string = 'Table';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'columns', 'rows' ];

    public get modelName(): string {
        // Method Implementation: simple
        return Table.modelName;
    }

    private _columns: Column[];

    private _rows: Row[];

    public get columns(): Column[] {
        // Method Implementation: simple
        return this._columns;
    }

    public get rows(): Row[] {
        // Method Implementation: simple
        return this._rows;
    }

    public set columns(value: Column[]) {
        // Method Implementation: simple
        this._columns = value;
        this._isColumnsDefined = true;
    }

    public set rows(value: Row[]) {
        // Method Implementation: simple
        this._rows = value;
        this._isRowsDefined = true;
    }

    private _isColumnsDefined: boolean;

    private _isRowsDefined: boolean;

    public get isColumnsDefined(): boolean {
        // Method Implementation: simple
        return this._isColumnsDefined;
    }

    public get isRowsDefined(): boolean {
        // Method Implementation: simple
        return this._isRowsDefined;
    }

    public constructor();
    public constructor(source: ITable);
    public constructor(source: Partial<Pick<Table, 'columns' | 'rows'>>);
    public constructor(source?: ITable | Partial<Pick<Table, 'columns' | 'rows'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isTableModel(source)) {
                if (source.isColumnsDefined) {
                    this._columns = source.columns;
                    this._isColumnsDefined = true;
                }
                else {
                    this._columns = [];
                    this._isColumnsDefined = true;
                }
                
                if (source.isRowsDefined) {
                    this._rows = source.rows;
                    this._isRowsDefined = true;
                }
                else {
                    this._rows = [];
                    this._isRowsDefined = true;
                }
            }
            else {
                if (source.columns !== undefined) {
                    this._columns = source.columns;
                    this._isColumnsDefined = true;
                }
                else {
                    this._columns = [];
                    this._isColumnsDefined = true;
                }
                
                if (source.rows !== undefined) {
                    this._rows = source.rows;
                    this._isRowsDefined = true;
                }
                else {
                    this._rows = [];
                    this._isRowsDefined = true;
                }
            }
        }
        else {
            this._columns = [];
            this._isColumnsDefined = true;
        
            this._rows = [];
            this._isRowsDefined = true;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return Table.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isColumnsDefined && this.columns !== undefined)
            definedProperties.push('columns');
        if (this.isRowsDefined && this.rows !== undefined)
            definedProperties.push('rows');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'columns':
                return this.columns;
            case 'rows':
                return this.rows;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isColumnsDefined && this.columns !== undefined) {
            if (this.columns !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('columns', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.columns.length));
                for (let i = 0; i < this.columns.length; i++) {
                    const item = this.columns[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('columns', 'null'));
            }
        }
        
        if (this.isRowsDefined && this.rows !== undefined) {
            if (this.rows !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('rows', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.rows.length));
                for (let i = 0; i < this.rows.length; i++) {
                    const item = this.rows[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('rows', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'columns':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.columns = [];
                
                    let item: Column = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: model
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            if (modelHeader.modelName === Column.modelName) {
                                item = new Column();
                                item.read(reader, modelHeader);
                            }
                            else {
                                throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${Column.modelName}'`);
                            }
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'columns' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'columns'`);
                        }
                
                        this.columns.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'columns' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'columns'`);
                }
        
                return true;
            case 'rows':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.rows = [];
                
                    let item: Row = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: model
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            if (modelHeader.modelName === Row.modelName) {
                                item = new Row();
                                item.read(reader, modelHeader);
                            }
                            else {
                                throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${Row.modelName}'`);
                            }
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'rows' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'rows'`);
                        }
                
                        this.rows.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'rows' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'rows'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // columns
        if (this.isColumnsDefined && this.columns !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.columns === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.columns.length);
            
                for (const item of this.columns) {
                    // Item Implementation: model
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // rows
        if (this.isRowsDefined && this.rows !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.rows === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.rows.length);
            
                for (const item of this.rows) {
                    // Item Implementation: model
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // columns
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: Column;
            
                    this.columns = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: model
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                item = new Column();
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.columns.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // rows
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: Row;
            
                    this.rows = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: model
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                item = new Row();
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.rows.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): Table {
        // Method Implementation: simple
        const schema = TableZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: TableZodSchemaShape): Table {
        // Method Implementation: parseZodSchema
        return new Table({
            columns: schema.columns?.map(i => Column.parseZodSchema(i)),
            rows: schema.rows?.map(i => Row.parseZodSchema(i))
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            columns: this.isColumnsDefined ? (this.columns?.map(item => item)) : undefined,
            rows: this.isRowsDefined ? (this.rows?.map(item => item)) : undefined,
        };
    }

    public clone(): Table {
        // Method Implementation: clone
        return new Table({
            columns: this.isColumnsDefined ? this.columns?.map(i => i.clone()) : undefined,
            rows: this.isRowsDefined ? this.rows?.map(i => i.clone()) : undefined
        });
    }
}

export class Column implements IColumn {
    public static readonly modelName: string = 'Column';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'name', 'dataType' ];

    public get modelName(): string {
        // Method Implementation: simple
        return Column.modelName;
    }

    private _name: string;

    private _dataType: DataTypes;

    public get name(): string {
        // Method Implementation: simple
        return this._name;
    }

    public get dataType(): DataTypes {
        // Method Implementation: simple
        return this._dataType;
    }

    public set name(value: string) {
        // Method Implementation: simple
        this._name = value;
        this._isNameDefined = true;
    }

    public set dataType(value: DataTypes) {
        // Method Implementation: simple
        this._dataType = value;
        this._isDataTypeDefined = true;
    }

    private _isNameDefined: boolean;

    private _isDataTypeDefined: boolean;

    public get isNameDefined(): boolean {
        // Method Implementation: simple
        return this._isNameDefined;
    }

    public get isDataTypeDefined(): boolean {
        // Method Implementation: simple
        return this._isDataTypeDefined;
    }

    public constructor();
    public constructor(source: IColumn);
    public constructor(source: Partial<Pick<Column, 'name' | 'dataType'>>);
    public constructor(source?: IColumn | Partial<Pick<Column, 'name' | 'dataType'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isColumnModel(source)) {
                if (source.isNameDefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.isDataTypeDefined) {
                    this._dataType = source.dataType;
                    this._isDataTypeDefined = true;
                }
                else {
                    this._dataType = DataTypes.String;
                    this._isDataTypeDefined = false;
                }
            }
            else {
                if (source.name !== undefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.dataType !== undefined) {
                    this._dataType = source.dataType;
                    this._isDataTypeDefined = true;
                }
                else {
                    this._dataType = DataTypes.String;
                    this._isDataTypeDefined = false;
                }
            }
        }
        else {
            this._name = '';
            this._isNameDefined = false;
        
            this._dataType = DataTypes.String;
            this._isDataTypeDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return Column.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isNameDefined && this.name !== undefined)
            definedProperties.push('name');
        if (this.isDataTypeDefined && this.dataType !== undefined)
            definedProperties.push('dataType');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'name':
                return this.name;
            case 'dataType':
                return this.dataType;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isNameDefined && this.name !== undefined) {
            if (this.name !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.name);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'null'));
            }
        }
        
        if (this.isDataTypeDefined && this.dataType !== undefined) {
            if (this.dataType !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('dataType', 'enum'));
        
                // Implementation: axon/write/enum
                writer.writeEnumValue(this.dataType);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('dataType', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'name':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.name = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'name' cannot be null`);
                    this.name = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'name'`);
                }
        
                return true;
            case 'dataType':
                // Implementation: axon/readProperty/enum
                if (propertyHeader.type === 'enum') {
                    this.dataType = reader.readEnumValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Property 'dataType' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'dataType'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // name
        if (this.isNameDefined && this.name !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.name === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.name);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // dataType
        if (this.isDataTypeDefined && this.dataType !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/enum
            writer.writeEnumValue(this.dataType);
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // name
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.name = <any>null;
                    break;
                case 1:
                    this.name = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // dataType
        if (reader.readBooleanValue())
        {
            // Implementation: enum
            this.dataType = reader.readEnumValue();
        }
    }

    public static parse(hash: unknown): Column {
        // Method Implementation: simple
        const schema = ColumnZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: ColumnZodSchemaShape): Column {
        // Method Implementation: parseZodSchema
        return new Column({
            name: schema.name,
            dataType: (() => {
            if (!schema.dataType)
                return;
        
            switch (schema.dataType) {
                case 'String':
                    return DataTypes.String;
                case 'Integer':
                    return DataTypes.Integer;
                case 'Decimal':
                    return DataTypes.Decimal;
                default:
                    throw new Error(`Enum type "schema.dataType" not recognized`);
            }
        })()
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            name: this.isNameDefined ? ((this.name === null ? undefined : this.name)) : undefined,
            dataType: this.isDataTypeDefined ? (DataTypes[this.dataType]) : undefined,
        };
    }

    public clone(): Column {
        // Method Implementation: clone
        return new Column({
            name: this.isNameDefined ? this.name : undefined,
            dataType: this.isDataTypeDefined ? this.dataType : undefined
        });
    }
}

export class Row implements IRow {
    public static readonly modelName: string = 'Row';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'fields' ];

    public get modelName(): string {
        // Method Implementation: simple
        return Row.modelName;
    }

    private _fields: AField[];

    public get fields(): AField[] {
        // Method Implementation: simple
        return this._fields;
    }

    public set fields(value: AField[]) {
        // Method Implementation: simple
        this._fields = value;
        this._isFieldsDefined = true;
    }

    private _isFieldsDefined: boolean;

    public get isFieldsDefined(): boolean {
        // Method Implementation: simple
        return this._isFieldsDefined;
    }

    public constructor();
    public constructor(source: IRow);
    public constructor(source: Partial<Pick<Row, 'fields'>>);
    public constructor(source?: IRow | Partial<Pick<Row, 'fields'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isRowModel(source)) {
                if (source.isFieldsDefined) {
                    this._fields = source.fields;
                    this._isFieldsDefined = true;
                }
                else {
                    this._fields = [];
                    this._isFieldsDefined = true;
                }
            }
            else {
                if (source.fields !== undefined) {
                    this._fields = source.fields;
                    this._isFieldsDefined = true;
                }
                else {
                    this._fields = [];
                    this._isFieldsDefined = true;
                }
            }
        }
        else {
            this._fields = [];
            this._isFieldsDefined = true;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return Row.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isFieldsDefined && this.fields !== undefined)
            definedProperties.push('fields');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'fields':
                return this.fields;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isFieldsDefined && this.fields !== undefined) {
            if (this.fields !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('fields', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.fields.length));
                for (let i = 0; i < this.fields.length; i++) {
                    const item = this.fields[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('fields', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'fields':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.fields = [];
                
                    let item: AField = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: abstractModel
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            item = <any>ModelClassFactory.createModel(modelHeader.modelName);
                            item.read(reader, modelHeader);
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'fields' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'fields'`);
                        }
                
                        this.fields.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'fields' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'fields'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // fields
        if (this.isFieldsDefined && this.fields !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.fields === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.fields.length);
            
                for (const item of this.fields) {
                    // Item Implementation: abstractModel
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                    
                        if (item instanceof StringField)
                            writer.writeShortValue(0);
                        else if (item instanceof IntegerField)
                            writer.writeShortValue(1);
                        else if (item instanceof DecimalField)
                            writer.writeShortValue(2);
                        else if (item instanceof BooleanField)
                            writer.writeShortValue(3);
                        else
                            throw new Error('Unsupported type implementation');
                    
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // fields
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: AField;
            
                    this.fields = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: abstractModel
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                switch (reader.readShortValue())
                                {
                                    case 0:
                                        item = new StringField();
                                        break;
                                    case 1:
                                        item = new IntegerField();
                                        break;
                                    case 2:
                                        item = new DecimalField();
                                        break;
                                    case 3:
                                        item = new BooleanField();
                                        break;
                                    default:
                                        throw new Error('Invalid abstract model code');
                                }
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
                        this.fields.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): Row {
        // Method Implementation: simple
        const schema = RowZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: RowZodSchemaShape): Row {
        // Method Implementation: parseZodSchema
        return new Row({
            fields: schema.fields?.map(i => AField.parseZodSchema(i))
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            fields: this.isFieldsDefined ? (this.fields?.map(item => item)) : undefined,
        };
    }

    public clone(): Row {
        // Method Implementation: clone
        return new Row({
            fields: this.isFieldsDefined ? this.fields?.map(i => i.clone()) : undefined
        });
    }
}

export abstract class AField implements IField {
    public static readonly modelName: string = 'Field';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'name' ];

    public get modelName(): string {
        // Method Implementation: simple
        return AField.modelName;
    }

    private _name: string;

    public get name(): string {
        // Method Implementation: simple
        return this._name;
    }

    public set name(value: string) {
        // Method Implementation: simple
        this._name = value;
        this._isNameDefined = true;
    }

    private _isNameDefined: boolean;

    public get isNameDefined(): boolean {
        // Method Implementation: simple
        return this._isNameDefined;
    }

    public constructor();
    public constructor(source: IField);
    public constructor(source: Partial<Pick<AField, 'name'>>);
    public constructor(source?: IField | Partial<Pick<AField, 'name'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isFieldModel(source)) {
                if (source.isNameDefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
            }
            else {
                if (source.name !== undefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
            }
        }
        else {
            this._name = '';
            this._isNameDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return AField.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isNameDefined && this.name !== undefined)
            definedProperties.push('name');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'name':
                return this.name;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isNameDefined && this.name !== undefined) {
            if (this.name !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.name);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'name':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.name = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'name' cannot be null`);
                    this.name = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'name'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // name
        if (this.isNameDefined && this.name !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.name === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.name);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // name
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.name = <any>null;
                    break;
                case 1:
                    this.name = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): AField {
        // Method Implementation: simple
        const schema = FieldZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: FieldZodSchemaShape): AField {
        // Method Implementation: parseAbstractZodSchema
        switch (schema.fieldType)
        {
            case 'stringField':
            case 'string':
                return StringField.parse(schema);
            case 'integerField':
            case 'integer':
                return IntegerField.parse(schema);
            case 'decimalField':
            case 'decimal':
                return DecimalField.parse(schema);
            case 'booleanField':
            case 'boolean':
                return BooleanField.parse(schema);
            default:
                throw new Error(`fieldType of ${ schema.fieldType } is not supported`);
        }
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            name: this.isNameDefined ? ((this.name === null ? undefined : this.name)) : undefined,
        };
    }

    public abstract clone(): AField;
}

export class StringField extends AField implements IStringField {
    public static readonly modelName: string = 'StringField';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'value' ];

    public get modelName(): string {
        // Method Implementation: simple
        return StringField.modelName;
    }

    private _value: string;

    public get value(): string {
        // Method Implementation: simple
        return this._value;
    }

    public set value(value: string) {
        // Method Implementation: simple
        this._value = value;
        this._isValueDefined = true;
    }

    private _isValueDefined: boolean;

    public get isValueDefined(): boolean {
        // Method Implementation: simple
        return this._isValueDefined;
    }

    public constructor();
    public constructor(source: IStringField);
    public constructor(source: Partial<Pick<StringField, 'value' | 'name'>>);
    public constructor(source?: IStringField | Partial<Pick<StringField, 'value' | 'name'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            super(source);
        
            if (isStringFieldModel(source)) {
                if (source.isValueDefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = '';
                    this._isValueDefined = false;
                }
            }
            else {
                if (source.value !== undefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = '';
                    this._isValueDefined = false;
                }
            }
        }
        else {
            super();
        
            this._value = '';
            this._isValueDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return [ ...super.getPropertyNames(), ...StringField.propertyNames ];
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [ ...super.getDefinedPropertyNames() ];
        
        if (this.isValueDefined && this.value !== undefined)
            definedProperties.push('value');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'value':
                return this.value;
            default:
                return super.getPropertyValue(propertyName);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        super.write(writer);
        
        if (this.isValueDefined && this.value !== undefined) {
            if (this.value !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.value);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'value':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.value = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'value' cannot be null`);
                    this.value = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'value'`);
                }
        
                return true;
            default:
                return super.readProperty(reader, propertyHeader);
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        super.slipstreamWrite(writer);
        
        // value
        if (this.isValueDefined && this.value !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.value === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.value);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        super.slipstreamRead(reader);
        
        // value
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.value = <any>null;
                    break;
                case 1:
                    this.value = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): StringField {
        // Method Implementation: simple
        const schema = StringFieldZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: StringFieldZodSchemaShape): StringField {
        // Method Implementation: parseZodSchema
        return new StringField({
            name: schema.name,
            value: schema.value
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            fieldType: 'string',
            ...super.toJSON(),
            value: this.isValueDefined ? ((this.value === null ? undefined : this.value)) : undefined,
        };
    }

    public clone(): StringField {
        // Method Implementation: clone
        return new StringField({
            name: this.isNameDefined ? this.name : undefined,
            value: this.isValueDefined ? this.value : undefined
        });
    }
}

export class IntegerField extends AField implements IIntegerField {
    public static readonly modelName: string = 'IntegerField';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'value' ];

    public get modelName(): string {
        // Method Implementation: simple
        return IntegerField.modelName;
    }

    private _value: number;

    public get value(): number {
        // Method Implementation: simple
        return this._value;
    }

    public set value(value: number) {
        // Method Implementation: simple
        this._value = value;
        this._isValueDefined = true;
    }

    private _isValueDefined: boolean;

    public get isValueDefined(): boolean {
        // Method Implementation: simple
        return this._isValueDefined;
    }

    public constructor();
    public constructor(source: IIntegerField);
    public constructor(source: Partial<Pick<IntegerField, 'value' | 'name'>>);
    public constructor(source?: IIntegerField | Partial<Pick<IntegerField, 'value' | 'name'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            super(source);
        
            if (isIntegerFieldModel(source)) {
                if (source.isValueDefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = 0;
                    this._isValueDefined = false;
                }
            }
            else {
                if (source.value !== undefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = 0;
                    this._isValueDefined = false;
                }
            }
        }
        else {
            super();
        
            this._value = 0;
            this._isValueDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return [ ...super.getPropertyNames(), ...IntegerField.propertyNames ];
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [ ...super.getDefinedPropertyNames() ];
        
        if (this.isValueDefined && this.value !== undefined)
            definedProperties.push('value');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'value':
                return this.value;
            default:
                return super.getPropertyValue(propertyName);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        super.write(writer);
        
        if (this.isValueDefined && this.value !== undefined) {
            if (this.value !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'int32'));
        
                // Implementation: axon/write/int32
                writer.writeIntegerValue(this.value);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'value':
                // Implementation: axon/readProperty/int32
                if (propertyHeader.type === 'int32') {
                    this.value = reader.readIntegerValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'value' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'value'`);
                }
        
                return true;
            default:
                return super.readProperty(reader, propertyHeader);
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        super.slipstreamWrite(writer);
        
        // value
        if (this.isValueDefined && this.value !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/int32
            writer.writeIntegerValue(this.value);
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        super.slipstreamRead(reader);
        
        // value
        if (reader.readBooleanValue())
        {
            // Implementation: int32
            this.value = reader.readIntegerValue();
        }
    }

    public static parse(hash: unknown): IntegerField {
        // Method Implementation: simple
        const schema = IntegerFieldZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: IntegerFieldZodSchemaShape): IntegerField {
        // Method Implementation: parseZodSchema
        return new IntegerField({
            name: schema.name,
            value: schema.value
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            fieldType: 'integer',
            ...super.toJSON(),
            value: this.isValueDefined ? (this.value) : undefined,
        };
    }

    public clone(): IntegerField {
        // Method Implementation: clone
        return new IntegerField({
            name: this.isNameDefined ? this.name : undefined,
            value: this.isValueDefined ? this.value : undefined
        });
    }
}

export class DecimalField extends AField implements IDecimalField {
    public static readonly modelName: string = 'DecimalField';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'value' ];

    public get modelName(): string {
        // Method Implementation: simple
        return DecimalField.modelName;
    }

    private _value: number;

    public get value(): number {
        // Method Implementation: simple
        return this._value;
    }

    public set value(value: number) {
        // Method Implementation: simple
        this._value = value;
        this._isValueDefined = true;
    }

    private _isValueDefined: boolean;

    public get isValueDefined(): boolean {
        // Method Implementation: simple
        return this._isValueDefined;
    }

    public constructor();
    public constructor(source: IDecimalField);
    public constructor(source: Partial<Pick<DecimalField, 'value' | 'name'>>);
    public constructor(source?: IDecimalField | Partial<Pick<DecimalField, 'value' | 'name'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            super(source);
        
            if (isDecimalFieldModel(source)) {
                if (source.isValueDefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = 0;
                    this._isValueDefined = false;
                }
            }
            else {
                if (source.value !== undefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = 0;
                    this._isValueDefined = false;
                }
            }
        }
        else {
            super();
        
            this._value = 0;
            this._isValueDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return [ ...super.getPropertyNames(), ...DecimalField.propertyNames ];
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [ ...super.getDefinedPropertyNames() ];
        
        if (this.isValueDefined && this.value !== undefined)
            definedProperties.push('value');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'value':
                return this.value;
            default:
                return super.getPropertyValue(propertyName);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        super.write(writer);
        
        if (this.isValueDefined && this.value !== undefined) {
            if (this.value !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'double'));
        
                // Implementation: axon/write/double
                writer.writeDoubleValue(this.value);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'value':
                // Implementation: axon/readProperty/double
                if (propertyHeader.type === 'double') {
                    this.value = reader.readDoubleValue();
                }
                else if (propertyHeader.type === 'single') {
                    this.value = reader.readFloatValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'value' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'value'`);
                }
        
                return true;
            default:
                return super.readProperty(reader, propertyHeader);
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        super.slipstreamWrite(writer);
        
        // value
        if (this.isValueDefined && this.value !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/double
            writer.writeDoubleValue(this.value);
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        super.slipstreamRead(reader);
        
        // value
        if (reader.readBooleanValue())
        {
            // Implementation: double
            this.value = reader.readDoubleValue();
        }
    }

    public static parse(hash: unknown): DecimalField {
        // Method Implementation: simple
        const schema = DecimalFieldZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: DecimalFieldZodSchemaShape): DecimalField {
        // Method Implementation: parseZodSchema
        return new DecimalField({
            name: schema.name,
            value: schema.value
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            fieldType: 'decimal',
            ...super.toJSON(),
            value: this.isValueDefined ? (this.value) : undefined,
        };
    }

    public clone(): DecimalField {
        // Method Implementation: clone
        return new DecimalField({
            name: this.isNameDefined ? this.name : undefined,
            value: this.isValueDefined ? this.value : undefined
        });
    }
}

export class BooleanField extends AField implements IBooleanField {
    public static readonly modelName: string = 'BooleanField';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'value' ];

    public get modelName(): string {
        // Method Implementation: simple
        return BooleanField.modelName;
    }

    private _value: boolean;

    public get value(): boolean {
        // Method Implementation: simple
        return this._value;
    }

    public set value(value: boolean) {
        // Method Implementation: simple
        this._value = value;
        this._isValueDefined = true;
    }

    private _isValueDefined: boolean;

    public get isValueDefined(): boolean {
        // Method Implementation: simple
        return this._isValueDefined;
    }

    public constructor();
    public constructor(source: IBooleanField);
    public constructor(source: Partial<Pick<BooleanField, 'value' | 'name'>>);
    public constructor(source?: IBooleanField | Partial<Pick<BooleanField, 'value' | 'name'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            super(source);
        
            if (isBooleanFieldModel(source)) {
                if (source.isValueDefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = false;
                    this._isValueDefined = false;
                }
            }
            else {
                if (source.value !== undefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = false;
                    this._isValueDefined = false;
                }
            }
        }
        else {
            super();
        
            this._value = false;
            this._isValueDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return [ ...super.getPropertyNames(), ...BooleanField.propertyNames ];
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [ ...super.getDefinedPropertyNames() ];
        
        if (this.isValueDefined && this.value !== undefined)
            definedProperties.push('value');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'value':
                return this.value;
            default:
                return super.getPropertyValue(propertyName);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        super.write(writer);
        
        if (this.isValueDefined && this.value !== undefined) {
            if (this.value !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'boolean'));
        
                // Implementation: axon/write/boolean
                writer.writeBooleanValue(this.value);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'value':
                // Implementation: axon/readProperty/boolean
                if (propertyHeader.type === 'boolean') {
                    this.value = reader.readBooleanValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'value' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'value'`);
                }
        
                return true;
            default:
                return super.readProperty(reader, propertyHeader);
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        super.slipstreamWrite(writer);
        
        // value
        if (this.isValueDefined && this.value !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/boolean
            writer.writeBooleanValue(this.value);
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        super.slipstreamRead(reader);
        
        // value
        if (reader.readBooleanValue())
        {
            // Implementation: boolean
            this.value = reader.readBooleanValue();
        }
    }

    public static parse(hash: unknown): BooleanField {
        // Method Implementation: simple
        const schema = BooleanFieldZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: BooleanFieldZodSchemaShape): BooleanField {
        // Method Implementation: parseZodSchema
        return new BooleanField({
            name: schema.name,
            value: schema.value
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            fieldType: 'boolean',
            ...super.toJSON(),
            value: this.isValueDefined ? (this.value) : undefined,
        };
    }

    public clone(): BooleanField {
        // Method Implementation: clone
        return new BooleanField({
            name: this.isNameDefined ? this.name : undefined,
            value: this.isValueDefined ? this.value : undefined
        });
    }
}

export class ModelClassFactory {
    public static createModel(modelName: string) {
        // Method Implementation: createModel
        switch (modelName) {
            case 'Table':
                return new Table();
            case 'Column':
                return new Column();
            case 'Row':
                return new Row();
            case 'StringField':
                return new StringField();
            case 'IntegerField':
                return new IntegerField();
            case 'DecimalField':
                return new DecimalField();
            case 'BooleanField':
                return new BooleanField();
            default:
                throw new Error(`Unknown model ${modelName}`);
        }
    }

    public static createFromModel(modelName: string, source: any) {
        // Method Implementation: createFromModel
        switch (modelName) {
            case 'Table':
                return new Table(source);
            case 'Column':
                return new Column(source);
            case 'Row':
                return new Row(source);
            case 'StringField':
                return new StringField(source);
            case 'IntegerField':
                return new IntegerField(source);
            case 'DecimalField':
                return new DecimalField(source);
            case 'BooleanField':
                return new BooleanField(source);
            default:
                throw new Error(`Unknown model ${modelName}`);
        }
    }

    public static parseModel(modelName: string, source: any) {
        // Method Implementation: parseModel
        switch (modelName) {
            case 'Table':
                return Table.parse(source);
            case 'Column':
                return Column.parse(source);
            case 'Row':
                return Row.parse(source);
            case 'StringField':
                return StringField.parse(source);
            case 'IntegerField':
                return IntegerField.parse(source);
            case 'DecimalField':
                return DecimalField.parse(source);
            case 'BooleanField':
                return BooleanField.parse(source);
            default:
                throw new Error(`Unknown model ${modelName}`);
        }
    }
}
